import { type AxiosResponse } from 'axios'
import retry from 'p-retry'

import agent from 'agent'
import { messageFromResponseError, parseUrl } from 'utils/strings'
import {
  buildTeamExternalEndpointConfigurationRequest,
  getEndpointConfigurationUrl,
  getIntegrationTypeFromObservabilityToolTypeAndDomain,
  isValidStatusCodeOrRange,
  teamExternalIntegrationsObject
} from 'utils/team-external-integrations/team-external-integrations-util'
import { ms } from 'utils/time/millisecond'

import type React from 'react'
import type {
  EndpointConfiguration,
  EndpointResponse,
  TeamExternalIntegration
} from 'types/api/status-checks/status-check'

type OnSuccess = (response: EndpointResponse) => void
type OnError = (responseBody: string | null, statusCode?: number | null) => void

export const MESSAGES = {
  failCanceled: 'Canceled',
  failInvocationId: 'Failed to get invocation id. Please try again.',
  failUrlParse: (url: string) => `integration url (${url}) is not a valid url`
}

/**
 * Handles the private network invocation response
 * @return {boolean} <b>true</b> if the invocation has finished processing, <b>false</b> otherwise
 */
function handlePrivateNetworkInvocationResponse(
  invocationResponse: AxiosResponse<{
    errorMessage?: string
    testEndpointResponse: EndpointResponse
  }>,
  onSuccess: OnSuccess,
  onError: OnError
) {
  if (invocationResponse.status === 204) {
    //status is No Content - not ready yet
    throw new Error('204 - No Content')
  }

  const { errorMessage, testEndpointResponse } = invocationResponse.data || {}

  if (errorMessage) {
    return onError(errorMessage, testEndpointResponse?.httpStatusCode)
  }

  if (
    isValidStatusCodeOrRange(testEndpointResponse?.httpStatusCode?.toString())
  ) {
    return onSuccess(testEndpointResponse)
  }

  return onError(
    testEndpointResponse?.responseBody ?? null,
    testEndpointResponse?.httpStatusCode
  )
}

function handleError(err: any, onError: OnError) {
  if (['CanceledError', 'AbortError'].includes(err?.name)) {
    return onError(MESSAGES.failCanceled)
  }

  if (err?.response) {
    return onError(messageFromResponseError(err), err.response.status ?? null)
  }

  if (err?.message) {
    return onError(err.message)
  }

  return onError(JSON.stringify(err))
}

export async function testTeamExternalIntegrationEndpointConfiguration({
  abortControllerRef,
  authenticate = false,
  endpointConfiguration,
  integration,
  statusCheckId,
  teamId,
  onError,
  onSuccess
}: {
  abortControllerRef: React.MutableRefObject<AbortController | undefined>
  authenticate?: boolean
  endpointConfiguration?: EndpointConfiguration
  integration: TeamExternalIntegration
  statusCheckId?: string
  teamId: string
  onError: OnError
  onSuccess: OnSuccess
}) {
  const { domain, observabilityToolType } = integration

  const teamIntegrationType =
    observabilityToolType &&
    getIntegrationTypeFromObservabilityToolTypeAndDomain(
      observabilityToolType,
      domain
    )

  const endpointConfigurationUrl =
    (await getEndpointConfigurationUrl(integration, teamId)) ||
    (teamIntegrationType
      ? teamExternalIntegrationsObject?.[teamIntegrationType]?.authUrl
      : undefined)

  if (
    !endpointConfigurationUrl ||
    parseUrl(endpointConfigurationUrl) === false
  ) {
    return onError(
      MESSAGES.failUrlParse(endpointConfigurationUrl ?? 'undefined')
    )
  }

  const requestData = buildTeamExternalEndpointConfigurationRequest(
    integration,
    endpointConfigurationUrl ?? '',
    statusCheckId,
    authenticate,
    endpointConfiguration
  )

  abortControllerRef.current = new AbortController()

  if (integration.isPrivateNetworkIntegration) {
    try {
      const { data } = await agent.post(
        `integration-invocations?teamId=${teamId}`,
        requestData,
        { signal: abortControllerRef.current.signal }
      )

      const invocationId = data?.invocationId ?? null

      if (invocationId === null) {
        return onError(MESSAGES.failInvocationId)
      }

      await retry(
        async function () {
          const invocationResponse = await agent.post<{
            errorMessage?: string
            testEndpointResponse: EndpointResponse
          }>(
            `integration-invocations/${invocationId}/run-test-endpoint?teamId=${teamId}`,
            null,
            { signal: abortControllerRef.current?.signal }
          )
          return handlePrivateNetworkInvocationResponse(
            invocationResponse,
            onSuccess,
            onError
          )
        },
        {
          maxTimeout: ms.seconds(20),
          minTimeout: 500,
          retries: 8,
          signal: abortControllerRef.current?.signal // returns 'AbortError'
        }
      )
      return
    } catch (err) {
      return handleError(err, onError)
    }
  }

  try {
    const { data } = await agent.post<{
      testEndpointResponse: EndpointResponse
    }>(`status-checks/run-test-endpoint?teamId=${teamId}`, requestData, {
      signal: abortControllerRef.current.signal
    })

    const { testEndpointResponse } = data ?? {}

    if (
      isValidStatusCodeOrRange(testEndpointResponse?.httpStatusCode?.toString())
    ) {
      return onSuccess(testEndpointResponse)
    }

    return onError(
      testEndpointResponse?.responseBody ?? null,
      testEndpointResponse?.httpStatusCode
    )
  } catch (err) {
    return handleError(err, onError)
  }
}
